import { createFileRoute, useNavigate, Navigate } from '@tanstack/react-router';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowRight, Activity, Clock, Sparkles } from 'lucide-react';

import { db } from '@/lib/db/instant';
import { LogoMark } from '@/components/Logo';

export const Route = createFileRoute('/')({
  component: Landing,
});

function Landing() {
  const { isLoading, user } = db.useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-bg">
        <LogoMark size={64} variant="dark" />
        <ActivityIndicator color="#FF5A1F" />
      </View>
    );
  }

  if (user) {
    return <Navigate to="/home" />;
  }

  return (
    <SafeAreaView className="flex-1 bg-bg">
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 48, paddingTop: 32, alignItems: 'center' }}
        showsVerticalScrollIndicator={false}
      >
       <View className="w-full max-w-md px-6 lg:max-w-5xl lg:px-12 lg:pt-12">
        <View className="mb-8 items-start lg:mb-16 lg:max-w-2xl">
          <LogoMark size={48} variant="dark" />
          <Text className="mt-4 text-2xl font-semibold tracking-tight text-text lg:mt-6 lg:text-5xl">
            Stay on your curve.
          </Text>
          <Text className="mt-2 text-sm leading-relaxed text-textDim lg:mt-4 lg:text-lg">
            halvn calculates exactly what you should inject — and when — for compounds where
            half-life matters. Built for monitored medical use.
          </Text>
          <View className="hidden lg:flex lg:mt-8 lg:flex-row lg:gap-3">
            <Pressable
              onPress={() => navigate({ to: '/sign-in' })}
              className="flex-row items-center justify-center gap-2 rounded-xl bg-text px-6 py-3.5 active:opacity-80"
            >
              <Text className="text-sm font-medium text-white">Get started</Text>
              <ArrowRight color="#FFFFFF" size={16} />
            </Pressable>
            <Pressable
              onPress={() => navigate({ to: '/sign-in' })}
              className="items-center justify-center rounded-xl border border-border bg-surface px-6 py-3.5 active:opacity-70"
            >
              <Text className="text-sm font-medium text-text">Sign in</Text>
            </Pressable>
          </View>
        </View>

        <View className="gap-3 lg:flex-row lg:gap-5">
          <Pillar
            icon={<Activity color="#FF5A1F" size={18} />}
            title="Pharmacokinetic math, not guesswork"
            body="Bateman one-compartment model with the half-lives of your compound. Sum across every past injection. See exactly what's in your system."
          />
          <Pillar
            icon={<Sparkles color="#FF5A1F" size={18} />}
            title="The right dose, every time"
            body="halvn solves for the dose that keeps you on the reference curve. Took more yesterday? Today's dose adjusts down. Late by a day? It compensates up."
          />
          <Pillar
            icon={<Clock color="#FF5A1F" size={18} />}
            title="Past, present, future — one timeline"
            body="Past doses scroll up. Now sits in the centre with a confirm-able recommendation. Upcoming projections dynamically regenerate as you log."
          />
        </View>

        <View className="lg:hidden">
          <Pressable
            onPress={() => navigate({ to: '/sign-in' })}
            className="mt-8 flex-row items-center justify-center gap-2 rounded-xl bg-text py-4 active:opacity-80"
          >
            <Text className="text-sm font-medium text-white">Get started</Text>
            <ArrowRight color="#FFFFFF" size={16} />
          </Pressable>

          <Pressable
            onPress={() => navigate({ to: '/sign-in' })}
            className="mt-3 self-center active:opacity-60"
            hitSlop={8}
          >
            <Text className="text-xs text-textDim underline">Already have an account? Sign in</Text>
          </Pressable>
        </View>

        <View className="mt-10 flex-row items-center justify-center gap-4 border-t border-border pt-6 lg:mt-16 lg:justify-start">
          <Pressable onPress={() => navigate({ to: '/privacy' })} hitSlop={8} className="active:opacity-60">
            <Text className="text-[11px] text-textMuted">Privacy</Text>
          </Pressable>
          <Text className="text-[11px] text-textMuted">·</Text>
          <Pressable onPress={() => navigate({ to: '/terms' })} hitSlop={8} className="active:opacity-60">
            <Text className="text-[11px] text-textMuted">Terms</Text>
          </Pressable>
          <Text className="text-[11px] text-textMuted">·</Text>
          <Text className="text-[11px] text-textMuted">© 2026 halvn</Text>
        </View>
       </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function Pillar({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <View className="rounded-2xl border border-border bg-surface p-4 lg:flex-1 lg:p-6">
      <View className="flex-row items-center gap-2 lg:items-start">
        <View className="h-8 w-8 items-center justify-center rounded-lg bg-accent/10 lg:h-10 lg:w-10">
          {icon}
        </View>
        <Text className="text-sm font-semibold text-text lg:mt-1.5 lg:text-base">{title}</Text>
      </View>
      <Text className="mt-2 text-xs leading-relaxed text-textDim lg:mt-3 lg:text-sm">{body}</Text>
    </View>
  );
}
